import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import axios from 'axios';
import { toast } from 'sonner';
import {
  TrendUp, PaperPlaneRight, SignOut, Robot, User, Spinner
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import Logo from '@/components/Logo';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChatAdvisor = () => {
  const navigate = useNavigate();
  const { user, logout, getAuthHeaders } = useAuth();
  const { t } = useLanguage();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [assessments, setAssessments] = useState([]);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => { fetchAssessments(); fetchChatHistory(); }, []);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const fetchAssessments = async () => {
    try {
      const response = await axios.get(`${API_URL}/assessments`, { headers: getAuthHeaders() });
      setAssessments(response.data.filter(a => a.status === 'completed'));
    } catch (error) { /* ignore */ }
  };

  const fetchChatHistory = async () => {
    try {
      const response = await axios.get(`${API_URL}/chat/history`, { params: { limit: 50 }, headers: getAuthHeaders() });
      setMessages(response.data);
    } catch (error) { /* ignore */ }
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;
    const userMessage = input.trim();
    setInput('');
    setLoading(true);
    const tempUserMsg = { id: Date.now(), role: 'user', content: userMessage, created_at: new Date().toISOString() };
    setMessages(prev => [...prev, tempUserMsg]);
    try {
      const response = await axios.post(`${API_URL}/chat`, { message: userMessage, assessment_id: selectedAssessment }, { headers: getAuthHeaders() });
      setMessages(prev => [...prev, response.data]);
    } catch (error) {
      toast.error(t('chat.error'));
      setMessages(prev => prev.filter(m => m.id !== tempUserMsg.id));
    } finally { setLoading(false); }
  };

  const handleKeyPress = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } };
  const handleLogout = () => { logout(); navigate('/'); };

  const suggestedQuestions = [t('chat.suggested.1'), t('chat.suggested.2'), t('chat.suggested.3'), t('chat.suggested.4')];

  return (
    <div className="min-h-screen bg-[#F4F4F5] flex flex-col">
      <header className="bg-white border-b border-[#E4E4E7]">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <Link to="/dashboard" className="flex items-center gap-2">
              <Logo variant="light" size="md" />
            </Link>
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/dashboard" className="text-[#52525B] hover:text-[#09090B] transition-colors">{t('nav.dashboard')}</Link>
              <Link to="/companies" className="text-[#52525B] hover:text-[#09090B] transition-colors">{t('nav.companies')}</Link>
              <Link to="/advisor" className="text-[#09090B] font-medium">{t('nav.advisor')}</Link>
            </nav>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-[#0F1B36] flex items-center justify-center">
                    <span className="text-white text-sm font-bold">{user?.first_name?.[0]}{user?.last_name?.[0]}</span>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleLogout} className="text-[#E60000]">
                  <SignOut size={16} className="mr-2" />{t('nav.signout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <div className="flex-1 max-w-4xl w-full mx-auto flex flex-col">
        <div className="px-6 py-4 border-b border-[#E4E4E7] bg-white">
          <div className="flex items-center gap-4">
            <label className="text-sm text-[#52525B]">{t('chat.context')}</label>
            <Select
              value={selectedAssessment?.toString() || '__general__'}
              onValueChange={(val) => setSelectedAssessment(val && val !== '__general__' ? parseInt(val) : null)}
            >
              <SelectTrigger className="w-64 rounded-none" data-testid="assessment-context-select">
                <SelectValue placeholder={t('chat.general')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__general__">{t('chat.general')}</SelectItem>
                {assessments.map((a) => (
                  <SelectItem key={a.id} value={a.id.toString()}>{a.company_name} ({a.assessment_type})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6" data-testid="chat-messages">
          {messages.length === 0 ? (
            <div className="text-center py-12">
              <Robot size={64} className="text-[#E4E4E7] mx-auto mb-4" />
              <h2 className="heading-4 text-[#09090B] mb-2">{t('chat.title')}</h2>
              <p className="text-[#52525B] mb-8 max-w-md mx-auto">{t('chat.description')}</p>
              <div className="flex flex-wrap justify-center gap-3">
                {suggestedQuestions.map((q, idx) => (
                  <button key={idx} onClick={() => setInput(q)}
                    className="px-4 py-2 border border-[#E4E4E7] text-sm text-[#52525B] hover:border-[#0F1B36] hover:text-[#0F1B36] transition-colors"
                    data-testid={`suggested-q-${idx}`}
                  >{q}</button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((msg) => (
              <div key={msg.id} className={`flex gap-4 ${msg.role === 'user' ? 'justify-end' : ''}`} data-testid={`chat-message-${msg.id}`}>
                {msg.role === 'assistant' && (
                  <div className="w-10 h-10 bg-[#0F1B36] flex items-center justify-center flex-shrink-0">
                    <Robot size={20} className="text-white" />
                  </div>
                )}
                <div className={`max-w-[70%] p-4 ${msg.role === 'user' ? 'bg-[#0F1B36] text-white' : 'bg-white border border-[#E4E4E7] text-[#09090B]'}`}>
                  <p className="whitespace-pre-wrap">{msg.content}</p>
                  <p className={`text-xs mt-2 ${msg.role === 'user' ? 'text-white/70' : 'text-[#52525B]'}`}>
                    {new Date(msg.created_at).toLocaleTimeString()}
                  </p>
                </div>
                {msg.role === 'user' && (
                  <div className="w-10 h-10 bg-[#F4F4F5] flex items-center justify-center flex-shrink-0">
                    <User size={20} className="text-[#52525B]" />
                  </div>
                )}
              </div>
            ))
          )}
          {loading && (
            <div className="flex gap-4">
              <div className="w-10 h-10 bg-[#0F1B36] flex items-center justify-center flex-shrink-0">
                <Robot size={20} className="text-white" />
              </div>
              <div className="bg-white border border-[#E4E4E7] p-4">
                <Spinner size={24} className="text-[#0F1B36] animate-spin" />
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="bg-white border-t border-[#E4E4E7] p-4">
          <div className="flex gap-4">
            <Textarea value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={handleKeyPress}
              placeholder={t('chat.input_placeholder')}
              className="flex-1 resize-none rounded-none min-h-[56px] max-h-[120px]"
              disabled={loading} data-testid="chat-input"
            />
            <Button onClick={handleSend} disabled={!input.trim() || loading}
              className="bg-[#0F1B36] hover:bg-[#00227A] text-white rounded-none px-6" data-testid="chat-send-btn"
            >
              <PaperPlaneRight size={20} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatAdvisor;
